export class ApiError extends Error {
  constructor(public code: string, public status: number, message: string, public retryAfter = 0) {
    super(message);
  }
}

export interface PlotRoleInfo { id: string; name: string; role: "owner" | "builder" }
export interface Session { id: string; email: string; kind: string; is_admin: boolean; display_name: string; owned_assets: number; plots: PlotRoleInfo[] }
/** One health check (spec 2o §3.4); ok false is a warning, never a refusal. */
export interface InspectCheck { rule: string; ok: boolean; value: number | string[]; limit: number | string[] }
/** Facts read from the GLB plus today's checks (spec 2o §3.5); null until the server has read the file. */
export interface InspectReport {
  clips: { name: string; duration: number; channels: number }[];
  skins: { joints: number; inverse_bind: boolean }[];
  textures: { w: number; h: number }[];
  node_count: number; tri_count: number; min_y: number; center_xz: [number, number]; size: [number, number, number];
  extensions_used: string[];
  checks: InspectCheck[];
}
export interface AssetInfo {
  code: string; name: string; owner_id: string; owner_name?: string; content_hash: string; size_bytes: number; tri_count: number;
  bounds_min: [number, number, number]; bounds_max: [number, number, number]; status: string; created_at: string;
  visibility: "private" | "shared" | "public"; license: string;
  inspect?: InspectReport | null;
}
export interface ShareInfo { account?: string; plot?: string }
export interface AssetDetail extends AssetInfo { used_in_plots: number; shares?: ShareInfo[] }
export interface LedgerEntry {
  asset_id: string; seq: number; kind: string; from: string; to: string; payload: string;
  prev_hash: string; hash: string; signature: string; signer_pubkey: string; created_at: string;
}
export interface HistoryResponse { asset_code: string; entries: LedgerEntry[]; keys: Record<string, string>; verified: boolean }
export interface PlotInfo {
  id: string; parcel_id?: string; name: string; kind: string; owner_id?: string; owner_name?: string;
  bounds: { min: [number, number]; max: [number, number] };
  height: number; capacity: number; per_account: number; placement_policy: string; consent_mode: string; used: number;
  live_version: number; draft_version: number;
}
export interface Collaborator { id: string; display_name: string; role: string }
export interface PlotDetail extends PlotInfo { collaborators: Collaborator[]; my_role?: "" | "owner" | "builder" }
export interface ParcelInfo { id: string; zone: [number, number]; bounds: { min: [number, number]; max: [number, number] }; taken: boolean }
export interface LayoutEntity {
  asset?: string; pos: [number, number, number]; yaw: number; scale?: number; role?: string; tags?: string[];
  components?: Record<string, unknown>;
}
export interface LayoutView { plot_id: string; stage: "live" | "draft"; version: number; live_version: number; has_draft: boolean; entities: LayoutEntity[] }

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T;
  const retryAfter = Number(res.headers.get("Retry-After")) || 0;
  let code = "INTERNAL", message = res.statusText;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body.error?.code) { code = body.error.code; message = body.error.message ?? message; }
  } catch { /* non-JSON body */ }
  throw new ApiError(code, res.status, message, retryAfter);
}

const json = (body: unknown): RequestInit => ({ method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export const getMe = () => api<Session>("/v1/me");
export const requestCode = (email: string) => api<{ code?: string }>("/v1/auth/code", json({ email }));
export const verifyCode = (email: string, code: string) => api<{ account: Session }>("/v1/auth/verify", json({ email, code }));
export const logout = () => api<void>("/v1/auth/logout", { method: "POST" });
export const getWorldConfig = () => api<{ hash: string; config: unknown }>("/v1/world/config");
export const getPlots = () => api<{ plots: PlotInfo[] }>("/v1/plots").then((r) => r.plots);
export const getMyAssets = () => api<{ assets: AssetInfo[] }>("/v1/me/assets").then((r) => r.assets);
export const getAsset = (code: string) => api<AssetInfo>(`/v1/assets/${encodeURIComponent(code)}`);
export const getHistory = (code: string) => api<HistoryResponse>(`/v1/assets/${encodeURIComponent(code)}/history`);
export const transferAsset = (code: string, to: string) => api<LedgerEntry>(`/v1/assets/${encodeURIComponent(code)}/transfer`, json({ to }));
const put = (body: unknown): RequestInit => ({ method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
const enc = encodeURIComponent;

export const getAssetDetail = (code: string) => api<AssetDetail>(`/v1/assets/${enc(code)}`);
export const setPolicy = (code: string, visibility: string, license: string, shares: ShareInfo[]) =>
  api<LedgerEntry>(`/v1/assets/${enc(code)}/policy`, put({ visibility, license, shares }));

export function publicAssetsURL(q: string, before?: string, limit = 20): string {
  const p = new URLSearchParams({ visibility: "public", limit: String(limit) });
  if (q.trim()) p.set("q", q.trim());
  if (before) p.set("before", before);
  return `/v1/assets?${p.toString()}`;
}
export const searchPublicAssets = (q: string, before?: string) => api<{ assets: AssetInfo[] }>(publicAssetsURL(q, before)).then((r) => r.assets);

export const getParcels = () => api<{ parcels: ParcelInfo[] }>("/v1/plots/parcels").then((r) => r.parcels);
export interface RoadRect { kind: "avenue" | "road"; min: [number, number]; max: [number, number] }
export const getRoads = () => api<{ hash: string; roads: RoadRect[] }>("/v1/world/roads").then((r) => r.roads ?? []);
export interface CompassMark { id: string; name: string; pos: [number, number] }
export interface CompassDoc { hide: ("estate" | "library")[]; marks: CompassMark[] }
export const getCompass = () => api<CompassDoc>("/v1/me/compass");
export const putCompass = (d: CompassDoc) => api<CompassDoc>("/v1/me/compass", { ...json(d), method: "PUT" });
export type HotbarSlot = { kind: "asset"; code: string } | { kind: "tool"; tool: string };
export interface SettingsDoc { hotbar: (HotbarSlot | null)[]; mouse: { sensitivity: number; invertY: boolean }; fov: number }
export const getSettings = () => api<SettingsDoc>("/v1/me/settings");
export const putSettings = (d: SettingsDoc) => api<SettingsDoc>("/v1/me/settings", { ...json(d), method: "PUT" });
export const movePlot = (id: string, parcelId: string) => api<PlotInfo>(`/v1/plots/${enc(id)}/move`, json({ parcel_id: parcelId }));
export const getPlot = (id: string) => api<PlotDetail>(`/v1/plots/${enc(id)}`);
export const addCollaborator = (plotId: string, account: string) =>
  api<{ collaborators: Collaborator[] }>(`/v1/plots/${enc(plotId)}/collaborators`, json({ account }));
export const removeCollaborator = (plotId: string, accountId: string) =>
  api<void>(`/v1/plots/${enc(plotId)}/collaborators/${enc(accountId)}`, { method: "DELETE" });
export const getLayout = (plotId: string, stage: "live" | "draft") => api<LayoutView>(`/v1/plots/${enc(plotId)}/layout?stage=${stage}`);
export const discardDraft = (plotId: string) => api<{ ok: boolean }>(`/v1/plots/${enc(plotId)}/discard-draft`, { method: "POST" });

/** Re-reads /v1/me so role-dependent UI (plots, removal rights) updates. */
export async function refreshSession(set: (s: Session) => void): Promise<void> {
  set(await getMe());
}
export const fileURL = (code: string) => `/v1/assets/${encodeURIComponent(code)}/file`;

/** XHR so we get upload progress; resolves with the created asset or rejects with ApiError. */
export function uploadAsset(file: File, name: string, onProgress: (fraction: number) => void): Promise<AssetInfo> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("name", name);
    form.append("file", file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/v1/assets");
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onerror = () => reject(new ApiError("INTERNAL", 0, "network error"));
    xhr.onload = () => {
      let body: { error?: { code?: string; message?: string } } | null = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as unknown as AssetInfo);
      else reject(new ApiError(body?.error?.code ?? "INTERNAL", xhr.status, body?.error?.message ?? xhr.statusText));
    };
    xhr.send(form);
  });
}

export interface MarkerTarget { entity_id: string; asset_code?: string; name?: string; role?: string; stage: string }
export interface MarkerPerson { id: string; display_name: string }
export interface MarkerView {
  id: string; version: number; pos: [number, number, number]; target: MarkerTarget | null; body: string;
  status: "open" | "done"; reply: string | null; created_by: MarkerPerson; resolved_by: MarkerPerson | null;
  created_at: string; updated_at: string; resolved_at: string | null;
}
export interface NewMarker { pos: [number, number, number]; target?: { entity_id: string }; body: string }
const markersPath = (plotId: string) => `/v1/plots/${enc(plotId)}/markers`;
export const listMarkers = (plotId: string) => api<{ markers: MarkerView[] }>(markersPath(plotId)).then((r) => r.markers);
export const createMarker = (plotId: string, m: NewMarker) => api<MarkerView>(markersPath(plotId), json(m));
export const editMarker = (plotId: string, id: string, body: string) =>
  api<MarkerView>(`${markersPath(plotId)}/${enc(id)}`, { ...json({ body }), method: "PATCH" });
export const deleteMarker = (plotId: string, id: string) => api<void>(`${markersPath(plotId)}/${enc(id)}`, { method: "DELETE" });

export interface MailMessage { id: string; from_agent: boolean; body: string; created_at: string; read_at: string | null }
export interface MailThread {
  agent: { id: string; display_name: string }; unread: number;
  last: { from_agent: boolean; preview: string; created_at: string } | null; checked_at: string | null;
}
export interface MailSummary { threads: MailThread[]; unread_total: number }
const mailPath = (agentId: string) => `/v1/me/mail/${enc(agentId)}`;
export const getMailSummary = () => api<MailSummary>("/v1/me/mail");
export const getMailThread = (agentId: string, before?: string) =>
  api<{ messages: MailMessage[]; has_more: boolean }>(before ? `${mailPath(agentId)}?before=${enc(before)}` : mailPath(agentId));
export const postMail = (agentId: string, body: string) => api<MailMessage>(mailPath(agentId), json({ body }));
export const markMailRead = (agentId: string, upTo: string) => api<void>(`${mailPath(agentId)}/read`, json({ up_to: upTo }));
